import { add } from '../../Context/dist/effect-Context.esm.js';
import { clockTag } from '../../internal/clock.esm.js';
import { currentRequestBatching, isEffect, flatMap as flatMap$2, currentTracerTimingEnabled, currentUnhandledErrorLogLevel } from '../../internal/core.esm.js';
import { currentServices } from '../../internal/defaultServices.esm.js';
import { fiberRefLocallyScopedWith as fiberRefLocallyScopedWith$2, fiberRefLocallyScoped as fiberRefLocallyScoped$1 } from '../../internal/fiberRuntime.esm.js';
import { LayerTypeId as LayerTypeId$1, isLayer as isLayer$1, isFresh as isFresh$1, build as build$1, buildWithScope as buildWithScope$1, catchAll as catchAll$1, catchAllCause as catchAllCause$1, context as context$1, die as die$1, dieSync as dieSync$1, discard as discard$1, fromEffect, fromEffectDiscard, fromEffectContext, extendScope as extendScope$1, fail as fail$1, failSync as failSync$1, failCause as failCause$1, failCauseSync as failCauseSync$1, flatMap as flatMap$1, flatten as flatten$1, fresh as fresh$1, fromFunction as fromFunction$1, launch as launch$1, map as map$1, mapError as mapError$1, match as match$1, matchCause as matchCause$1, memoize as memoize$1, merge as merge$1, mergeAll as mergeAll$1, orDie as orDie$1, orElse as orElse$1, passthrough as passthrough$1, project as project$1, provide as provide$1, locallyEffect as locallyEffect$1, fiberRefLocally, fiberRefLocallyWith, fiberRefLocallyScoped, fiberRefLocallyScopedWith as fiberRefLocallyScopedWith$1, provideMerge as provideMerge$1, retry as retry$1, scope as scope$1, scoped as scoped$1, scopedDiscard as scopedDiscard$1, scopedContext as scopedContext$1, service as service$1, succeed as succeed$1, succeedContext as succeedContext$1, suspend as suspend$1, sync as sync$1, syncContext as syncContext$1, tap as tap$1, tapError as tapError$1, tapErrorCause as tapErrorCause$1, toRuntime as toRuntime$1, use as use$1, useMerge as useMerge$1, zipWithPar as zipWithPar$1, unwrapEffect as unwrapEffect$1, unwrapScoped as unwrapScoped$1, withSpan as withSpan$1, withParentSpan as withParentSpan$1 } from '../../internal/layer.esm.js';
import { setConfigProvider as setConfigProvider$1, setParentSpan as setParentSpan$1, setSpan as setSpan$1, setTracer as setTracer$1 } from '../../internal/layer/circular.esm.js';
import { currentCacheEnabled, currentCache } from '../../internal/query.esm.js';
import { currentScheduler } from '../../Scheduler/dist/effect-Scheduler.esm.js';

/**
 * A `Layer<RIn, E, ROut>` describes how to build one or more services in your
 * application. Services can be injected into effects via
 * `Effect.provideService`. Effects can require services via `Effect.service`.
 *
 * Layer can be thought of as recipes for producing bundles of services, given
 * their dependencies (other services).
 *
 * Construction of services can be effectful and utilize resources that must be
 * acquired and safely released when the services are done being utilized.
 *
 * By default layers are shared, meaning that if the same layer is used twice
 * the layer will only be allocated a single time.
 *
 * Because of their excellent composition properties, layers are the idiomatic
 * way in Effect-TS to create services that depend on other services.
 *
 * @since 2.0.0
 */
/**
 * @since 2.0.0
 * @category symbols
 */
const LayerTypeId = LayerTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 */

/**
 * Returns `true` if the specified value is a `Layer`, `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isLayer = isLayer$1;

/**
 * Returns `true` if the specified `Layer` is a fresh version that will not be
 * shared, `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isFresh = isFresh$1;

/**
 * Builds a layer into a scoped value.
 *
 * @since 2.0.0
 * @category destructors
 */
const build = build$1;

/**
 * Builds a layer into an `Effect` value. Any resources associated with this
 * layer will be released when the specified scope is closed unless their scope
 * has been extended. This allows building layers where the lifetime of some of
 * the services output by the layer exceed the lifetime of the effect the
 * layer is provided to.
 *
 * @since 2.0.0
 * @category destructors
 */
const buildWithScope = buildWithScope$1;

/**
 * Recovers from all errors.
 *
 * @since 2.0.0
 * @category error handling
 */
const catchAll = catchAll$1;

/**
 * Recovers from all errors.
 *
 * @since 2.0.0
 * @category error handling
 */
const catchAllCause = catchAllCause$1;

/**
 * Constructs a `Layer` that passes along the specified context as an
 * output.
 *
 * @since 2.0.0
 * @category constructors
 */
const context = context$1;

/**
 * Constructs a layer that dies with the specified defect.
 *
 * @since 2.0.0
 * @category constructors
 */
const die = die$1;

/**
 * Constructs a layer that dies with the specified defect.
 *
 * @since 2.0.0
 * @category constructors
 */
const dieSync = dieSync$1;

/**
 * Replaces the layer's output with `void` and includes the layer only for its
 * side-effects.
 *
 * @since 2.0.0
 * @category mapping
 */
const discard = discard$1;

/**
 * Constructs a layer from the specified effect.
 *
 * @since 2.0.0
 * @category constructors
 */
const effect = fromEffect;

/**
 * Constructs a layer from the specified effect discarding it's output.
 *
 * @since 2.0.0
 * @category constructors
 */
const effectDiscard = fromEffectDiscard;

/**
 * Constructs a layer from the specified effect, which must return one or more
 * services.
 *
 * @since 2.0.0
 * @category constructors
 */
const effectContext = fromEffectContext;

/**
 * Extends the scope of this layer, returning a new layer that when provided
 * to an effect will not immediately release its associated resources when
 * that effect completes execution but instead when the scope the resulting
 * effect depends on is closed.
 *
 * @since 2.0.0
 * @category utils
 */
const extendScope = extendScope$1;

/**
 * Constructs a layer that fails with the specified error.
 *
 * @since 2.0.0
 * @category constructors
 */
const fail = fail$1;

/**
 * Constructs a layer that fails with the specified error.
 *
 * @since 2.0.0
 * @category constructors
 */
const failSync = failSync$1;

/**
 * Constructs a layer that fails with the specified cause.
 *
 * @since 2.0.0
 * @category constructors
 */
const failCause = failCause$1;

/**
 * Constructs a layer that fails with the specified cause.
 *
 * @since 2.0.0
 * @category constructors
 */
const failCauseSync = failCauseSync$1;

/**
 * Constructs a layer dynamically based on the output of this layer.
 *
 * @since 2.0.0
 * @category sequencing
 */
const flatMap = flatMap$1;

/**
 * Flattens layers nested in the context of an effect.
 *
 * @since 2.0.0
 * @category sequencing
 */
const flatten = flatten$1;

/**
 * Creates a fresh version of this layer that will not be shared.
 *
 * @since 2.0.0
 * @category utils
 */
const fresh = fresh$1;
const fromFunction = fromFunction$1;

/**
 * Builds this layer and uses it until it is interrupted. This is useful when
 * your entire application is a layer, such as an HTTP server.
 *
 * @since 2.0.0
 * @category conversions
 */
const launch = launch$1;

/**
 * Returns a new layer whose output is mapped by the specified function.
 *
 * @since 2.0.0
 * @category mapping
 */
const map = map$1;

/**
 * Returns a layer with its error channel mapped using the specified function.
 *
 * @since 2.0.0
 * @category mapping
 */
const mapError = mapError$1;

/**
 * Feeds the error or output services of this layer into the input of either
 * the specified `failure` or `success` layers, resulting in a new layer with
 * the inputs of this layer, and the error or outputs of the specified layer.
 *
 * @since 2.0.0
 * @category folding
 */
const match = match$1;

/**
 * Feeds the error or output services of this layer into the input of either
 * the specified `failure` or `success` layers, resulting in a new layer with
 * the inputs of this layer, and the error or outputs of the specified layer.
 *
 * @since 2.0.0
 * @category folding
 */
const matchCause = matchCause$1;

/**
 * Returns a scoped effect that, if evaluated, will return the lazily computed
 * result of this layer.
 *
 * @since 2.0.0
 * @category utils
 */
const memoize = memoize$1;

/**
 * Combines this layer with the specified layer, producing a new layer that
 * has the inputs and outputs of both.
 *
 * @since 2.0.0
 * @category utils
 */
const merge = merge$1;

/**
 * Merges all the layers together in parallel.
 *
 * @since 2.0.0
 * @category zipping
 */
const mergeAll = mergeAll$1;

/**
 * Translates effect failure into death of the fiber, making all failures
 * unchecked and not a part of the type of the layer.
 *
 * @since 2.0.0
 * @category error handling
 */
const orDie = orDie$1;

/**
 * Executes this layer and returns its output, if it succeeds, but otherwise
 * executes the specified layer.
 *
 * @since 2.0.0
 * @category error handling
 */
const orElse = orElse$1;

/**
 * Returns a new layer that produces the outputs of this layer but also
 * passes through the inputs.
 *
 * @since 2.0.0
 * @category utils
 */
const passthrough = passthrough$1;

/**
 * Projects out part of one of the services output by this layer using the
 * specified function.
 *
 * @since 2.0.0
 * @category utils
 */
const project = project$1;

/**
 * Feeds the output services of this builder into the input of the specified
 * builder, resulting in a new builder with the inputs of this builder as
 * well as any leftover inputs, and the outputs of the specified builder.
 *
 * @since 2.0.0
 * @category utils
 */
const provide = provide$1;

/**
 * @since 2.0.0
 * @category utils
 */
const locallyEffect = locallyEffect$1;

/**
 * @since 2.0.0
 * @category utils
 */
const locally = fiberRefLocally;

/**
 * @since 2.0.0
 * @category utils
 */
const locallyWith = fiberRefLocallyWith;

/**
 * @since 2.0.0
 * @category utils
 */
const locallyScoped = fiberRefLocallyScoped;

/**
 * @since 2.0.0
 * @category utils
 */
const fiberRefLocallyScopedWith = fiberRefLocallyScopedWith$1;

/**
 * Feeds the output services of this layer into the input of the specified
 * layer, resulting in a new layer with the inputs of this layer, and the
 * outputs of both layers.
 *
 * @since 2.0.0
 * @category utils
 */
const provideMerge = provideMerge$1;

/**
 * Retries constructing this layer according to the specified schedule.
 *
 * @since 2.0.0
 * @category retrying
 */
const retry = retry$1;

/**
 * A layer that constructs a scope and closes it when the workflow the layer
 * is provided to completes execution, whether by success, failure, or
 * interruption. This can be used to close a scope when providing a layer to a
 * workflow.
 *
 * @since 2.0.0
 * @category constructors
 */
const scope = scope$1;

/**
 * Constructs a layer from the specified scoped effect.
 *
 * @since 2.0.0
 * @category constructors
 */
const scoped = scoped$1;

/**
 * Constructs a layer from the specified scoped effect.
 *
 * @since 2.0.0
 * @category constructors
 */
const scopedDiscard = scopedDiscard$1;

/**
 * Constructs a layer from the specified scoped effect, which must return one
 * or more services.
 *
 * @since 2.0.0
 * @category constructors
 */
const scopedContext = scopedContext$1;

/**
 * Constructs a layer that accesses and returns the specified service from the
 * context.
 *
 * @since 2.0.0
 * @category constructors
 */
const service = service$1;

/**
 * Constructs a layer from the specified value.
 *
 * @since 2.0.0
 * @category constructors
 */
const succeed = succeed$1;

/**
 * Constructs a layer from the specified value, which must return one or more
 * services.
 *
 * @since 2.0.0
 * @category constructors
 */
const succeedContext = succeedContext$1;

/**
 * Lazily constructs a layer. This is useful to avoid infinite recursion when
 * creating layers that refer to themselves.
 *
 * @since 2.0.0
 * @category constructors
 */
const suspend = suspend$1;

/**
 * Lazily constructs a layer from the specified value.
 *
 * @since 2.0.0
 * @category constructors
 */
const sync = sync$1;

/**
 * Lazily constructs a layer from the specified value, which must return one or more
 * services.
 *
 * @since 2.0.0
 * @category constructors
 */
const syncContext = syncContext$1;

/**
 * Performs the specified effect if this layer succeeds.
 *
 * @since 2.0.0
 * @category sequencing
 */
const tap = tap$1;

/**
 * Performs the specified effect if this layer fails.
 *
 * @since 2.0.0
 * @category sequencing
 */
const tapError = tapError$1;

/**
 * Performs the specified effect if this layer fails.
 *
 * @since 2.0.0
 * @category sequencing
 */
const tapErrorCause = tapErrorCause$1;

/**
 * Converts a layer that requires no services into a scoped runtime, which can
 * be used to execute effects.
 *
 * @since 2.0.0
 * @category conversions
 */
const toRuntime = toRuntime$1;

/**
 * Feeds the output services of this builder into the input of the specified
 * builder, resulting in a new builder with the inputs of this builder as
 * well as any leftover inputs, and the outputs of the specified builder.
 *
 * @since 2.0.0
 * @category utils
 */
const use = use$1;

/**
 * Feeds the output services of this layer into the input of the specified
 * layer, resulting in a new layer with the inputs of this layer, and the
 * outputs of both layers.
 *
 * @since 2.0.0
 * @category utils
 */
const useMerge = useMerge$1;

/**
 * Combines this layer the specified layer, producing a new layer that has the
 * inputs of both, and the outputs of both combined using the specified
 * function.
 *
 * @since 2.0.0
 * @category zipping
 */
const zipWithPar = zipWithPar$1;

/**
 * @since 2.0.0
 * @category utils
 */
const unwrapEffect = unwrapEffect$1;

/**
 * @since 2.0.0
 * @category utils
 */
const unwrapScoped = unwrapScoped$1;

/**
 * @since 2.0.0
 * @category clock
 */
const setClock = clock => scopedDiscard(fiberRefLocallyScopedWith$2(currentServices, add(clockTag, clock)));

/**
 * Sets the current `ConfigProvider`.
 *
 * @since 2.0.0
 * @category config
 */
const setConfigProvider = setConfigProvider$1;

/**
 * Adds the provided span to the span stack.
 *
 * @since 2.0.0
 * @category tracing
 */
const setParentSpan = setParentSpan$1;

/**
 * @since 2.0.0
 * @category requests & batching
 */
const setRequestBatching = requestBatching => scopedDiscard(fiberRefLocallyScoped$1(currentRequestBatching, requestBatching));

/**
 * @since 2.0.0
 * @category requests & batching
 */
const setRequestCaching = requestCaching => scopedDiscard(fiberRefLocallyScoped$1(currentCacheEnabled, requestCaching));

/**
 * @since 2.0.0
 * @category requests & batching
 */
const setRequestCache = cache => scopedDiscard(isEffect(cache) ? flatMap$2(cache, x => fiberRefLocallyScoped$1(currentCache, x)) : fiberRefLocallyScoped$1(currentCache, cache));

/**
 * @since 2.0.0
 * @category scheduler
 */
const setScheduler = scheduler => scopedDiscard(fiberRefLocallyScoped$1(currentScheduler, scheduler));

/**
 * Create and add a span to the current span stack.
 *
 * The span is ended when the Layer is released.
 *
 * @since 2.0.0
 * @category tracing
 */
const setSpan = setSpan$1;

/**
 * Create a Layer that sets the current Tracer
 *
 * @since 2.0.0
 * @category tracing
 */
const setTracer = setTracer$1;

/**
 * @since 2.0.0
 * @category tracing
 */
const setTracerTiming = enabled => scopedDiscard(fiberRefLocallyScoped$1(currentTracerTimingEnabled, enabled));

/**
 * @since 2.0.0
 * @category logging
 */
const setUnhandledErrorLogLevel = level => scopedDiscard(fiberRefLocallyScoped$1(currentUnhandledErrorLogLevel, level));

/**
 * @since 2.0.0
 * @category tracing
 */
const withSpan = withSpan$1;

/**
 * @since 2.0.0
 * @category tracing
 */
const withParentSpan = withParentSpan$1;

export { LayerTypeId, build, buildWithScope, catchAll, catchAllCause, context, die, dieSync, discard, effect, effectContext, effectDiscard, extendScope, fail, failCause, failCauseSync, failSync, fiberRefLocallyScopedWith, flatMap, flatten, fresh, fromFunction as function, isFresh, isLayer, launch, locally, locallyEffect, locallyScoped, locallyWith, map, mapError, match, matchCause, memoize, merge, mergeAll, orDie, orElse, passthrough, project, provide, provideMerge, retry, scope, scoped, scopedContext, scopedDiscard, service, setClock, setConfigProvider, setParentSpan, setRequestBatching, setRequestCache, setRequestCaching, setScheduler, setSpan, setTracer, setTracerTiming, setUnhandledErrorLogLevel, succeed, succeedContext, suspend, sync, syncContext, tap, tapError, tapErrorCause, toRuntime, unwrapEffect, unwrapScoped, use, useMerge, withParentSpan, withSpan, zipWithPar };
